/**
 * SiteHeader — shared, sticky customer header (hotfix-1-169).
 *
 * Previously every customer page hand-rolled its own <header>, so the
 * "How it works" link only appeared in the landing footer and vanished once a
 * customer browsed into a competition. This component centralises the customer
 * top nav so "How it works" (and the auth controls) are persistent everywhere.
 *
 * Pattern, classes and label conventions intentionally mirror the original
 * LandingPage header — do NOT invent a new look.
 *
 * Nav order:
 *   Logged-OUT: Partners → /become-a-partner · Sign in → /login ·
 *               How it works → /how-it-works · Register Free → /register (CTA)
 *   Logged-IN:  How it works → /how-it-works · Loyalty → /dashboard/loyalty ·
 *               Partners → /dashboard/partner · Log out (button)
 *
 * Note: the logged-out Partners link routes to the light /become-a-partner info
 * page (NOT straight to /register) and no longer sets pending_partner_intent —
 * that intent flag is now set by the CTA on the info page itself.
 */
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const navLinkClass =
  'flex min-h-[44px] cursor-pointer items-center rounded-button px-2 text-sm font-semibold text-violet-700 transition hover:bg-violet-50 sm:px-3';

export function SiteHeader() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-20 flex items-center justify-between border-b border-gray-100 bg-white/95 px-4 py-3 backdrop-blur sm:px-6">
      <Link
        to="/"
        className="flex items-center gap-2 font-heading text-lg font-bold text-violet-700"
      >
        🏆 Win a Prize
      </Link>
      <nav className="flex items-center gap-1 sm:gap-2" aria-label="Primary">
        {user ? (
          <>
            <Link to="/how-it-works" className={navLinkClass}>
              {t('landing.nav_how_it_works')}
            </Link>
            <Link to="/dashboard/loyalty" className={navLinkClass}>
              Loyalty
            </Link>
            {/* v4-2-4: logged-in partner entry point — the full partner bank. */}
            <Link to="/dashboard/partner" className={navLinkClass}>
              {t('landing.nav_partners')}
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="flex min-h-[44px] cursor-pointer items-center rounded-button px-2 text-sm font-semibold text-text-muted transition hover:text-text-primary sm:px-3"
            >
              Log out
            </button>
          </>
        ) : (
          <>
            {/* Light info FIRST — /become-a-partner explains the program before
                sending the visitor to the register form (hotfix-1-169). */}
            <Link to="/become-a-partner" className={navLinkClass}>
              {t('landing.nav_partners')}
            </Link>
            <Link to="/login" className={navLinkClass}>
              {t('landing.footer_login')}
            </Link>
            {/* "How it works" sits immediately before the Register Free CTA. */}
            <Link to="/how-it-works" className={navLinkClass}>
              {t('landing.nav_how_it_works')}
            </Link>
            <Link
              to="/register"
              className="flex min-h-[44px] cursor-pointer items-center rounded-button bg-violet-600 px-3 text-sm font-bold text-white transition hover:bg-violet-700 sm:px-4"
            >
              {t('landing.cta_register')}
            </Link>
          </>
        )}
      </nav>
    </header>
  );
}

export default SiteHeader;
